import appInfo from "@common/appInfo";
import { type InputTypeProps } from "@common/declarations";
import withDropdownHOC, { type WithDropdownCmpProps, type WithDropdownToggleProps } from "@common/hocs/withDropdown";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import withValueHOC from "@common/hocs/withValue";
import date from "@common/lib/date";
import classnames from "classnames";
import moment, { type Moment } from "moment";
import * as React from "react";
import DTP from "./Picker";
import { toInputValue, toOutputValue } from "./lib";

export type ValueType = moment.Moment | Date | string | null;

type DateTimePickerPropsInner = InputTypeProps<ValueType> & {
  className?: string;
  name?: string;
  isDisabled?: boolean;
  hasTime?: boolean;
  timeOnly?: boolean;

  minDate?: ValueType;
  maxDate?: ValueType;

  format?: string;
  displayFormat?: string;
  isUtcTime?: boolean;
  showClear?: boolean;
  style?: React.CSSProperties;
};

const DateTimePickerToggle = React.memo((props: WithDropdownToggleProps & DateTimePickerPropsInner) => (
  <div
    ref={props.toggleRef}
    className={classnames("date_time_picker_toggle", props.className, {
      open: props.isOpen,
      disabled: props.isDisabled
    })}
    data-name={props.qa || props.name}
    onClick={props.isDisabled ? undefined : props.toggleOpen}
    style={props.style}
  >
    {toInputValue(props.value, props.isUtcTime)?.format(
      props.displayFormat ||
        props.format ||
        (!!props.timeOnly ? "HH:mm" : props.hasTime === false ? date.DATE_FORMAT : date.DATE_TIME_FORMAT)
    ) || " "}
  </div>
));

class DateTimePickerContent extends React.PureComponent<WithDropdownCmpProps & DateTimePickerPropsInner> {
  public render() {
    const props = this.props;
    return (
      <DTP
        className={"date_time_picker"}
        showClear={props.showClear ?? false}
        showTimePicker={props.hasTime}
        showCalendarPicker={!props.timeOnly}
        minDate={toInputValue(props.minDate || null, props.isUtcTime)}
        maxDate={toInputValue(props.maxDate || null, props.isUtcTime)}
        onChange={this._onChange}
        moment={
          toInputValue(this.props.value, props.isUtcTime) ||
          (props.isUtcTime ? moment.utc() : moment.tz(appInfo.business.timeZoneIana))
        } /* IMPORTANT: underlying DTP does not support nulls */
      />
    );
  }

  private _onChange = (value: Moment | null): void => {
    this.props.onChange(toOutputValue(this.props, value));
  };
}

export type DateTimePickerProps = DateTimePickerPropsInner;
const DateTimePickerDropdown = withDropdownHOC<DateTimePickerProps>(DateTimePickerToggle, DateTimePickerContent);
const DateTimePickerBase = React.memo((props: DateTimePickerProps) => (
  <DateTimePickerDropdown {...props} align={ContentAlign.Right} maxHeight="unset" />
));

const DateTimePicker = withValueHOC<ValueType, DateTimePickerProps>(DateTimePickerBase);
export default DateTimePicker;
